import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bot } from "lucide-react";
import { AssistantChatMessage } from "../models/AssistantChatMessage";

interface AssistantChatThreadProps {
  messages: AssistantChatMessage[];
  thinking: boolean;
  errorMessage?: string;
}

/** The conversation so far: instructor bubbles right, assistant bubbles left. */
export function AssistantChatThread({ messages, thinking, errorMessage }: AssistantChatThreadProps) {
  const { t } = useTranslation();
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages.length, thinking]);

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <Bot size={24} strokeWidth={2} />
        </span>
        <p className="max-w-md text-sm text-grey-50 dark:text-grey-70">{t("assistant.emptyHint")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {messages.map((message, index) => (
        <div
          key={index}
          className={
            message.role === "user"
              ? "ml-auto w-fit max-w-[85%] rounded-2xl bg-primary-90 px-4 py-2.5 text-primary-20 dark:bg-primary-30 dark:text-primary-90"
              : "w-fit max-w-[85%] rounded-2xl bg-grey-95 px-4 py-2.5 dark:bg-primary-20 dark:text-white"
          }
        >
          <p className="whitespace-pre-wrap text-sm">{message.text}</p>
        </div>
      ))}
      {thinking && (
        <div
          className="w-fit rounded-2xl bg-grey-95 px-4 py-2.5 dark:bg-primary-20"
          role="status"
          aria-live="polite"
        >
          <span className="flex items-center gap-2 text-sm text-grey-50 dark:text-grey-70">
            <span
              className="animate-spin rounded-full h-4 w-4 border-2 border-primary-40 border-t-transparent"
              aria-hidden="true"
            />
            {t("assistant.thinking")}
          </span>
        </div>
      )}
      {errorMessage && (
        <div className="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
          {errorMessage}
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
}
